import type { CardData } from './types';

// Declarative effect read: a card with `take_only_singleton` may be taken by
// an exact-value match but is excluded from subset-sum combinations.
function isTakeOnlySingleton(card: CardData): boolean {
  return !!card.effects?.some(e => e.kind === 'take_only_singleton');
}

// Declarative read of the `freeze` stateful effect: while frozenTurns > 0 the
// card cannot be captured at all (neither exact match nor subset-sum).
function isFrozen(card: CardData): boolean {
  return (card.frozenTurns ?? 0) > 0;
}

// Calculates what a card takes from the table
export function resolveTake(cardValue: number, tableCards: CardData[]): CardData[] {
  // Frozen cards are completely untouchable for the lock duration — drop them
  // from every consideration below.
  const onTable = tableCards.filter(c => !isFrozen(c));

  // 1. Try to take a single card of the exact same value (singleton effects
  // do NOT block this — by definition they're still takeable as exact match).
  const exactMatch = onTable.find(c => c.value === cardValue);
  if (exactMatch) {
    return [exactMatch];
  }

  // 2. Look for combinations that sum exactly to cardValue.
  // Cards flagged take_only_singleton are skipped from the subset-sum pool.
  const subsetPool = onTable.filter(c => !isTakeOnlySingleton(c));
  const combinations = getCombinationsThatSumTo(cardValue, subsetPool);
  if (combinations.length > 0) {
    // Return the first valid combination (in a real game, if there are multiple, standard
    // scopa rules apply or player chooses. Here we take the first found deterministically).
    return combinations[0];
  }

  return [];
}

// Basic recursive subset sum
function getCombinationsThatSumTo(target: number, cards: CardData[]): CardData[][] {
  const result: CardData[][] = [];
  
  function backtrack(start: number, currentCombo: CardData[], currentSum: number) {
    if (currentSum === target) {
      result.push([...currentCombo]);
      // Scopa rules: usually you want combination of least cards or prioritize certain suits,
      // but retrieving any valid is acceptable for now.
      return;
    }
    if (currentSum > target) {
      return;
    }
    for (let i = start; i < cards.length; i++) {
      currentCombo.push(cards[i]);
      backtrack(i + 1, currentCombo, currentSum + cards[i].value);
      currentCombo.pop();
    }
  }

  backtrack(0, [], 0);
  // Sort results: prioritize fewer cards, just as a heuristic.
  result.sort((a, b) => a.length - b.length);
  return result;
}
